import { useContext, useEffect, useState } from 'react';

import Alert from '@/components/Alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { ChainlitContext } from 'client-types/*';

// What POST /login answers when a device must be approved first
// (backend/chainlit/two_step_api.py login_gate).
export interface TwoStepChallenge {
  methods: Array<'totp' | 'backup' | 'email'>;
  email_hint: string | null;
  trust_days: number;
}

type Method = 'totp' | 'backup' | 'email';

interface Props {
  challenge: TwoStepChallenge;
  onSignedIn: () => void;
  onCancel: () => void;
}

// Plain fetch, not the API client: its error handler would show a toast on
// top of the message this form already shows, and its 401 handler navigates.
export async function postAuth(
  url: string,
  body: Record<string, unknown> = {}
): Promise<any> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      typeof json?.detail === 'string'
        ? json.detail
        : 'Something went wrong. Please try again.'
    );
  }
  return json;
}

export function TwoStepForm({ challenge, onSignedIn, onCancel }: Props) {
  const apiClient = useContext(ChainlitContext);
  const first: Method = challenge.methods.includes('totp') ? 'totp' : 'email';
  const [method, setMethod] = useState<Method>(first);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const sendEmail = async () => {
    setBusy(true);
    setError('');
    try {
      const json = await postAuth(
        apiClient.buildEndpoint('/auth/two-step/email')
      );
      setEmailSent(true);
      setInfo(`We sent a code to ${json.email_hint}. It works for 10 minutes.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Email only: send the code as soon as the screen opens.
  useEffect(() => {
    if (method === 'email' && !emailSent) {
      sendEmail();
    }
  }, [method]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await postAuth(apiClient.buildEndpoint('/auth/two-step/verify'), {
        method,
        code,
        remember
      });
      onSignedIn();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchTo = (m: Method) => {
    setMethod(m);
    setCode('');
    setError('');
    setInfo('');
  };

  const title =
    method === 'email'
      ? 'Check your email'
      : method === 'backup'
        ? 'Use a backup code'
        : 'One more step';
  const hint =
    method === 'email'
      ? `Type the 6-digit code we sent to ${challenge.email_hint}.`
      : method === 'backup'
        ? 'Type one of the backup codes you saved when you set up your account. Each code works once.'
        : 'This is the first time you sign in from this device. Open your authenticator app and type the 6 digits it shows for BuduCloud Signals.';

  return (
    <form onSubmit={submit} className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="text-sm text-muted-foreground">{hint}</p>
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}
      {info && !error ? <Alert variant="info">{info}</Alert> : null}

      <div className="grid gap-2">
        <Label htmlFor="two-step-code">
          {method === 'backup' ? 'Backup code' : 'Code'}
        </Label>
        <Input
          id="two-step-code"
          autoFocus
          autoComplete="one-time-code"
          inputMode={method === 'backup' ? 'text' : 'numeric'}
          disabled={busy}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="text-center tracking-[0.3em]"
        />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="two-step-remember"
          checked={remember}
          onCheckedChange={(v) => setRemember(v === true)}
        />
        <Label htmlFor="two-step-remember" className="font-normal">
          Remember this device for {challenge.trust_days} days
        </Label>
      </div>

      <Button type="submit" className="w-full" disabled={busy || !code.trim()}>
        Sign in
      </Button>

      <div className="flex flex-col gap-1 text-sm">
        {method === 'email' ? (
          <Button
            type="button"
            variant="link"
            disabled={busy}
            onClick={sendEmail}
          >
            Send the code again
          </Button>
        ) : null}
        {method !== 'totp' && challenge.methods.includes('totp') ? (
          <Button type="button" variant="link" onClick={() => switchTo('totp')}>
            Use my authenticator app
          </Button>
        ) : null}
        {method !== 'email' && challenge.methods.includes('email') ? (
          <Button
            type="button"
            variant="link"
            onClick={() => {
              setEmailSent(false);
              switchTo('email');
            }}
          >
            I don't have my phone: email me a code
          </Button>
        ) : null}
        {method !== 'backup' && challenge.methods.includes('backup') ? (
          <Button
            type="button"
            variant="link"
            onClick={() => switchTo('backup')}
          >
            Use a backup code
          </Button>
        ) : null}
        <Button type="button" variant="link" onClick={onCancel}>
          Back to sign in
        </Button>
      </div>
    </form>
  );
}
